import React, { useState, useEffect, useRef } from 'react';
import { eventTracker } from './DataTracking';
import { getApiUrl } from '../lib/getApiUrl';
import { useAuthFetch } from '../lib/useAuthFetch';
import SharedHeaderCustomer from './SharedHeaderCustomer';
// CB:BEGIN homeowner-inline-help-import
import InlineHelpShortcut from './help/InlineHelpShortcut';
// CB:END homeowner-inline-help-import

interface ContractorBidsProps {
  onNavigate: (screen: string) => void;
}

interface Bid {
  id: string;
  projectId: string;
  contractorId: string | null;
  amount: number;
  amountCents?: number; // Add amountCents field
  isAccepted: boolean;
  isFake: boolean;
  createdAt: string;
  note?: string | null;
  scopeNotes?: string | null;
  earliestStartDate?: string | null;
  estimatedCompletionDate?: string | null;
  warrantyLaborYears?: number | null;
  warrantyMaterialYears?: number | null;
  [k: string]: unknown;
}

/** Structured quote summary with labeled fields (no subtotal in summary). */
export interface QuoteSummaryData {
  lineItems?: string;
  timeline?: string;
  scope?: string;
  warranty?: string;
}

/** Format YYYY-MM-DD to "MAR 03, 2026" for display. */
function formatTimelineDate(dateStr: string): string {
  const s = String(dateStr).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s + 'T12:00:00');
  if (Number.isNaN(d.getTime())) return s;
  const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const day = d.getDate().toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${month} ${day}, ${year}`;
}

/** Parse contractor quote JSON from bid.note; returns labeled summary object or null. */
function quoteSummaryFromNote(note: string | null | undefined): QuoteSummaryData | null {
  if (!note || typeof note !== 'string' || !note.trim()) return null;
  const trimmed = note.trim();
  if (trimmed[0] !== '{') return null; // not JSON
  try {
    const data = JSON.parse(trimmed) as Record<string, unknown>;
    const out: QuoteSummaryData = {};
    if (data.lineItems && Array.isArray(data.lineItems)) {
      const withDesc = (data.lineItems as Array<{ description?: string }>)
        .map((i) => (i.description || 'Item').trim())
        .filter(Boolean);
      if (withDesc.length) out.lineItems = withDesc.join(' • ');
    }
    if (data.earliestStartDate && data.estimatedCompletionDate) {
      const start = formatTimelineDate(String(data.earliestStartDate));
      const end = formatTimelineDate(String(data.estimatedCompletionDate));
      out.timeline = `${start} – ${end}`;
    }
    if (data.scopeNotes && String(data.scopeNotes).trim()) {
      out.scope = String(data.scopeNotes).trim();
    }
    if (data.warrantyMaterialYears || data.warrantyLaborYears) {
      const w: string[] = [];
      if (data.warrantyMaterialYears) w.push(`${data.warrantyMaterialYears}-yr materials`);
      if (data.warrantyLaborYears) w.push(`${data.warrantyLaborYears}-yr labor`);
      if (w.length) out.warranty = w.join(', ');
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

const LABEL_STYLE = { fontWeight: 600 as const, marginRight: 6, color: '#374151' };

function QuoteSummaryBlock({ summary, fallback }: { summary: QuoteSummaryData | null; fallback?: string }) {
  // Never render summary object as a React child - only use its string fields
  if (summary != null && typeof summary === 'object' && !Array.isArray(summary)) {
    const lineItems = typeof summary.lineItems === 'string' ? summary.lineItems : '';
    const timeline = typeof summary.timeline === 'string' ? summary.timeline : '';
    const scope = typeof summary.scope === 'string' ? summary.scope : '';
    const warranty = typeof summary.warranty === 'string' ? summary.warranty : '';
    if (lineItems || timeline || scope || warranty) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lineItems ? <div><span style={LABEL_STYLE}>Line items:</span><span style={{ color: '#6b7280' }}>{lineItems}</span></div> : null}
          {timeline ? <div><span style={LABEL_STYLE}>Timeline:</span><span style={{ color: '#6b7280' }}>{timeline}</span></div> : null}
          {scope ? <div><span style={LABEL_STYLE}>Scope:</span><span style={{ color: '#6b7280' }}>{scope}</span></div> : null}
          {warranty ? <div><span style={LABEL_STYLE}>Warranty:</span><span style={{ color: '#6b7280' }}>{warranty}</span></div> : null}
        </div>
      );
    }
  }
  if (fallback != null && typeof fallback === 'string' && fallback.trim()) {
    return (
      <div><span style={LABEL_STYLE}>Scope:</span><span style={{ color: '#6b7280' }}>{fallback.trim()}</span></div>
    );
  }
  return null;
}

const ContractorBids: React.FC<ContractorBidsProps> = ({ onNavigate }) => {
  const authFetch = useAuthFetch();
  const [showPopup, setShowPopup] = useState(false);
  // CB:BEGIN bid-details-state
  const [showDetails, setShowDetails] = useState(false);
  const [detailsBid, setDetailsBid] = useState<any | null>(null);
  // CB:END bid-details-state
  const [selectedBid, setSelectedBid] = useState<{ id: string; contractor: string; amount: string } | null>(null);
  const [sortBy, setSortBy] = useState<'price-low' | 'price-high' | 'rating' | 'timeline'>('price-low');
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [projectAddress, setProjectAddress] = useState<string>('');
  const filterRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch real bid data from backend
  useEffect(() => {
    // Track contractor bids page view
    eventTracker.track('contractor_bids_view');
    
    const fetchBids = async () => {
      try {
        setLoading(true);
        const activeProjectId = localStorage.getItem('active_project_id');
        let realBids: any[] = [];
        
        // CB:BEGIN fetch-real-bids-first
        // Fetch real bids from API first
        if (activeProjectId && activeProjectId !== '0' && activeProjectId !== 'demo-project') {
          const { getApiUrl } = await import('../lib/getApiUrl');
          const response = await authFetch(getApiUrl(`/api/projects/${activeProjectId}/bids`));
          if (response.ok) {
            const data = await response.json();
            const bidsArray = Array.isArray(data.bids) ? data.bids : (Array.isArray(data) ? data : []);
            // Transform real bids to include contractor info and format correctly
            realBids = bidsArray.map((bid: any) => ({
              id: bid.id,
              projectId: bid.projectId,
              contractorId: bid.contractorId,
              amount: bid.amount || 0,
              amountCents: bid.amount || bid.amountCents || 0,
              isAccepted: bid.isAccepted || false,
              isFake: false, // CB:BEGIN mark-real-bids - Mark real bids so they're not overwritten
              createdAt: bid.createdAt || new Date().toISOString(),
              contractor: bid.contractor,
              note: bid.note,
              scopeNotes: bid.scopeNotes,
              earliestStartDate: bid.earliestStartDate,
              estimatedCompletionDate: bid.estimatedCompletionDate,
              warrantyLaborYears: bid.warrantyLaborYears,
              warrantyMaterialYears: bid.warrantyMaterialYears,
              status: bid.status
            }));
            console.log('✅ Fetched real bids for contractor-bids page:', realBids.length);
            const projRes = await fetch(getApiUrl(`/api/projects/${activeProjectId}`));
            if (projRes.ok) {
              const proj = await projRes.json();
              setProjectAddress(proj.address || proj.project?.address || '');
            }
          } else if (response.status === 404) {
            // Project doesn't exist - clear invalid ID
            console.log(`⚠️ Project ${activeProjectId} not found, clearing from localStorage`);
            localStorage.removeItem('active_project_id');
          }
        }
        // CB:END fetch-real-bids-first
        
        // CB:BEGIN only-generate-fake-if-no-real-bids
        // Only generate fake bids if no real bids were found
        if (realBids.length === 0) {
        // Generate realistic fake bids based on quick estimate (production-style)
        const generateFakeBids = () => {
          // Get actual estimate from localStorage
          let baseEstimate = 15000; // Default fallback
          try {
            const savedEstimate = localStorage.getItem('quickEstimate');
            if (savedEstimate) {
              const estimateData = JSON.parse(savedEstimate);
              if (estimateData?.details?.bands?.midCents) {
                baseEstimate = estimateData.details.bands.midCents / 100;
              }
            }
          } catch (error) {
            console.warn('Could not parse saved estimate, using default:', error);
          }
          
          // Generate 3 realistic bids around the estimate (production-style)
          const base = Math.floor(baseEstimate * 0.8 + Math.random() * baseEstimate * 0.4); // 80-120% of estimate
          const fakeBids: Bid[] = [
            {
              id: 'fake-bid-1',
              projectId: 'fake-project',
              contractorId: 'fake-contractor-1',
              amount: base,
              amountCents: base * 100,
              isAccepted: false,
              isFake: true,
              createdAt: new Date().toISOString()
            },
            {
              id: 'fake-bid-2',
              projectId: 'fake-project',
              contractorId: 'fake-contractor-2',
              amount: base + 450,
              amountCents: (base + 450) * 100,
              isAccepted: false,
              isFake: true,
              createdAt: new Date().toISOString()
            },
            {
              id: 'fake-bid-3',
              projectId: 'fake-project',
              contractorId: 'fake-contractor-3',
              amount: base + 900,
              amountCents: (base + 900) * 100,
              isAccepted: false,
              isFake: true,
              createdAt: new Date().toISOString()
            }
          ];
          
          setBids(fakeBids);
        };
        
        generateFakeBids();
        } else {
          // Use real bids
          setBids(realBids);
        }
        // CB:END only-generate-fake-if-no-real-bids
      } catch (error) {
        console.error('Error fetching bids:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBids();
  }, []);

  const handleViewContractor = (bidId: string, contractorName: string) => {
    console.log('🔍 Viewing contractor details:', { bidId, contractorName });
    eventTracker.track('contractor_details_viewed');
    const bid = bids.find(b => b.id === bidId);
    setDetailsBid(bid || null);
    setShowDetails(true);
  };

  const handleAcceptBid = (bidId: string, contractor: string, amount: string) => {
    // Open confirm modal first; do not mark accepted yet
    console.log('🔍 handleAcceptBid called:', { bidId, contractor, amount });
    eventTracker.track('bid_accept_modal_opened');
    setSelectedBid({ id: bidId, contractor, amount });
    setShowPopup(true);
    console.log('🔍 showPopup set to true, selectedBid:', { id: bidId, contractor, amount });
  };

  const handleConfirmAccept = async () => {
    if (!selectedBid) return;
    try {
      const activeProjectId = localStorage.getItem('active_project_id');
      if (!activeProjectId) {
        console.error('No active project ID found');
        return;
      }

      // Mark bid accepted ONLY after confirmation
      const response = await authFetch(getApiUrl(`/api/projects/${activeProjectId}/accept-bid`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bidId: selectedBid.id }),
      });

      if (!response.ok) {
        console.error('Failed to accept bid');
        alert('Failed to accept bid. Please try again.');
        return;
      }

      // CB:BEGIN escrow-created-event
      // Dispatch event that escrow account was created (created when bid is accepted)
      try {
        window.dispatchEvent(new CustomEvent('cb:escrow-created', { 
          detail: { projectId: activeProjectId } 
        }));
      } catch {}
      // CB:END escrow-created-event

      eventTracker.track('bid_accepted');
      // Ensure PaymentScreen has the state it needs before navigation
      try {
        const activeProjectId = localStorage.getItem('active_project_id');
        if (activeProjectId) {
          localStorage.setItem('active_project_id', String(activeProjectId)); // reaffirm
        }
        if (selectedBid?.id) {
          localStorage.setItem('__cb_lastAcceptedBidId', String(selectedBid.id));
        }
        // Block any global auto-redirects momentarily during handoff
        (window as any).__CB_BLOCK_AUTO_NAV = true;
        setTimeout(() => { try { delete (window as any).__CB_BLOCK_AUTO_NAV; } catch {} }, 5000);
      } catch {}

      // Refresh bids to reflect acceptance state
      const bidsResp = await authFetch(getApiUrl(`/api/projects/${activeProjectId}/bids`));
      if (bidsResp.ok) {
        const bidsData = await bidsResp.json();
        setBids(bidsData.bids || []);
      }

      setShowPopup(false);
      setSelectedBid(null);
      // Navigate to payment screen (clean screen name only)
      console.log('▶️ Navigating to payment-screen');
      onNavigate('payment-screen');
    } catch (error) {
      console.error('Error accepting bid:', error);
      alert('Error accepting bid. Please try again.');
    }
  };

  const handleCancelAccept = () => {
    setShowPopup(false);
    setSelectedBid(null);
  };

  // CB:BEGIN get-contractor-name-real-or-fake
  // Get contractor name from real bid or generate fake name
  const getContractorName = (bid: Bid, index: number) => {
    // If real bid, use contractor company name
    if (!bid.isFake && bid.contractor) {
      const name = bid.contractor.company || bid.contractor.user?.email || 'Contractor';
      // Remove "Contractor" suffix if present
      return String(name).replace(/\s*contractor\b/i, '').trim() || 'Contractor';
    }
    // For fake bids, generate demo names
    const names = [
      'Elite Roofing Solutions',
      'Nashville Roofing Co.',
      'Precision Roofing',
      'Premier Roofing Services',
      'Quality Roofing Group'
    ];
    return names[index % names.length];
  };
  // CB:END get-contractor-name-real-or-fake

  // CB:BEGIN get-bid-details-real-defaults
  // Generate neutral fallback details (no fake ratings/reviews or marketing copy).
  const getBidDetails = (bid: Bid, index: number) => {
    // Calculate rough comparison to any saved estimate (optional, used only for internal hints).
    let estimateComparison = '';
    try {
      const savedEstimate = localStorage.getItem('quickEstimate');
      if (savedEstimate) {
        const estimateData = JSON.parse(savedEstimate);
        const midCents = estimateData?.details?.bands?.midCents;
        if (typeof midCents === 'number' && midCents > 0) {
          const baseEstimate = midCents / 100;
          const bidAmount = (bid.amountCents || bid.amount || 0) / 100;
          const percentageDiff = ((bidAmount - baseEstimate) / baseEstimate) * 100;
          if (percentageDiff < 0) {
            estimateComparison = `${Math.abs(percentageDiff).toFixed(0)}% below estimate`;
          } else if (percentageDiff > 0) {
            estimateComparison = `${percentageDiff.toFixed(0)}% above estimate`;
          } else {
            estimateComparison = 'At estimate';
          }
        }
      }
    } catch {
      // Ignore parsing errors; fallback text is optional.
    }

    return {
      rating: null,
      reviews: null,
      description: '',
      timeline: 'Timeline provided in quote',
      warranty: '',
      paymentTerms: '',
      startDate: '',
      estimateComparison,
    } as {
      rating: number | null;
      reviews: number | null;
      description: string;
      timeline: string;
      warranty: string;
      paymentTerms: string;
      startDate: string;
      estimateComparison: string;
    };
  };
  // CB:END get-bid-details-real-defaults

  // Sort bids based on selected criteria
  const sortedBids = [...bids].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.amount - b.amount;
      case 'price-high':
        return b.amount - a.amount;
      case 'rating': {
        // For demo, use index-based rating
        const aIndex = bids.indexOf(a);
        const bIndex = bids.indexOf(b);
        const aDetails = getBidDetails(a, aIndex);
        const bDetails = getBidDetails(b, bIndex);
        return bDetails.rating - aDetails.rating;
      }
      case 'timeline': {
        // Extract number of days from timeline string for sorting
        const aIndex2 = bids.indexOf(a);
        const bIndex2 = bids.indexOf(b);
        const aDetails2 = getBidDetails(a, aIndex2);
        const bDetails2 = getBidDetails(b, bIndex2);
        const aDays = parseInt(aDetails2.timeline.split('-')[0]);
        const bDays = parseInt(bDetails2.timeline.split('-')[0]);
        return aDays - bDays;
      }
      default:
        return a.amount - b.amount;
    }
  });

  // If there is an accepted bid, only show that one
  const accepted = bids.find(b => b.isAccepted);
  const visibleBids = accepted ? [accepted] : sortedBids;

  return (
    <div className="container">
      <SharedHeaderCustomer onNavigate={onNavigate} />
      <div className="content">
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          {/* CB:BEGIN homeowner-inline-help-bids */}
          <InlineHelpShortcut
            onNavigate={onNavigate}
            sectionId="viewing-bids"
            label="Need help comparing and selecting bids?"
          />
          {/* CB:END homeowner-inline-help-bids */}
          
          <div className="alert alert-info" style={{ marginTop: 40, marginBottom: 32, textAlign: 'center' }}>
            <div><strong>We have picked the top 3 contractor bids in your area!</strong></div>
            <div>All contractors are verified, licensed, and ready to start your project!</div>
          </div>

          {/* Filter Section */}
          <div 
            ref={filterRef}
            style={{ 
              background: '#f9fafb', 
              padding: '16px 20px', 
              borderRadius: '8px', 
              marginTop: '32px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              position: 'relative'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 28, margin: 0 }}>Your Project Bids</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>Sort By</span>
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M1 4h14M3 8h10M5 12h6" 
                      stroke="#374151" 
                      strokeWidth="2" 
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Dropdown Menu */}
            {showFilterMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '20px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '200px',
                marginTop: '8px'
              }}>
                <div style={{ padding: '8px 0' }}>
                  <button
                    onClick={() => {
                      setSortBy('price-low');
                      setShowFilterMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Price: Low to High
                  </button>
                  <button
                    onClick={() => {
                      setSortBy('price-high');
                      setShowFilterMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Price: High to Low
                  </button>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 18, color: '#6b7280' }}>Loading bids...</div>
            </div>
          ) : visibleBids.length > 0 ? (
            <div style={{ display: 'grid', gap: 24 }}>
              {visibleBids.map((bid, index) => {
                const contractorName = getContractorName(bid, index);
                const bidDetails = getBidDetails(bid, index);
                // Homeowner sees total they pay: contractor bid + 10% platform fee
                const contractorCents = bid.amountCents || bid.amount || 0;
                const totalWithFeeDollars = (contractorCents / 100) * 1.10;
                const amountDisplay = `$${totalWithFeeDollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const isAccepted = bid.isAccepted;
                
                // CB:BEGIN homeowner-bid-card-revamp
                const rawQuoteSummary = quoteSummaryFromNote(bid.note);
                const quoteSummary: QuoteSummaryData | null =
                  rawQuoteSummary != null && typeof rawQuoteSummary === 'object' && !Array.isArray(rawQuoteSummary)
                    ? {
                        lineItems: typeof rawQuoteSummary.lineItems === 'string' ? rawQuoteSummary.lineItems : undefined,
                        timeline: typeof rawQuoteSummary.timeline === 'string' ? rawQuoteSummary.timeline : undefined,
                        scope: typeof rawQuoteSummary.scope === 'string' ? rawQuoteSummary.scope : undefined,
                        warranty: typeof rawQuoteSummary.warranty === 'string' ? rawQuoteSummary.warranty : undefined,
                      }
                    : null;
                const timelineStr = (bid.earliestStartDate && bid.estimatedCompletionDate)
                  ? `${new Date(bid.earliestStartDate).toLocaleDateString()} – ${new Date(bid.estimatedCompletionDate).toLocaleDateString()}`
                  : bidDetails.timeline;
                const warrantyStr = (bid.warrantyMaterialYears || bid.warrantyLaborYears)
                  ? [
                      bid.warrantyMaterialYears ? `${bid.warrantyMaterialYears}-year materials` : '',
                      bid.warrantyLaborYears ? `${bid.warrantyLaborYears}-year labor` : '',
                    ].filter(Boolean).join(', ')
                  : (quoteSummary?.warranty || '—');
                const startDateStr = bid.estimatedStartDate
                  ? new Date(bid.estimatedStartDate).toLocaleDateString()
                  : bid.earliestStartDate
                    ? new Date(bid.earliestStartDate).toLocaleDateString()
                    : bidDetails.startDate;

                return (
                  <div 
                    key={bid.id} 
                    className="dashboard-card"
                    style={{
                      border: isAccepted ? '2px solid #10b981' : '1px solid #e5e7eb',
                      backgroundColor: isAccepted ? '#f0fdf4' : 'white',
                      padding: 20,
                      borderRadius: 10,
                    }}
                  >
                    {/* Contractor + price header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>{contractorName}</h3>
                          {isAccepted && (
                            <span style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              ACCEPTED
                            </span>
                          )}
                        </div>
                        <p style={{ margin: '0 0 2px 0', color: '#6b7280', fontSize: 13 }}>
                          Licensed • Insured roofing contractor
                        </p>
                        {projectAddress && (
                          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>{projectAddress}</p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: isAccepted ? '#10b981' : '#059669', lineHeight: 1.2 }}>
                          {amountDisplay}
                        </div>
                      </div>
                    </div>

                    {/* Key decision facts: timeline, warranty, start */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 14, padding: '12px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Project timeline</p>
                        <p style={{ margin: 0, fontSize: 14, color: '#111827', fontWeight: 500 }}>{timelineStr}</p>
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Warranty</p>
                        <p style={{ margin: 0, fontSize: 14, color: '#111827' }}>{warrantyStr}</p>
                        {(bid.warrantyTransferable !== undefined || bid.warrantyNotes) && (
                          <div style={{ marginTop: 6 }}>
                            {bid.warrantyTransferable !== undefined && (
                              <span style={{ fontSize: 12, color: '#6b7280' }}>{bid.warrantyTransferable ? 'Transferable' : 'Not transferable'}</span>
                            )}
                            {bid.warrantyNotes && (
                              <details style={{ marginTop: 4 }}>
                                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#2563eb' }}>Warranty details</summary>
                                <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>{bid.warrantyNotes}</p>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Earliest start</p>
                        <p style={{ margin: 0, fontSize: 14, color: '#111827' }}>{startDateStr}</p>
                      </div>
                    </div>

                    {/* What's included (quote summary with labels) */}
                    <div style={{ marginBottom: 14 }}>
                      <p style={{ margin: '0 0 6px 0', fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px' }}>What’s included</p>
                      <QuoteSummaryBlock summary={quoteSummary} fallback={bid.scopeNotes ?? bidDetails.paymentTerms} />
                    </div>

                    {/* Actions: compact row */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                      {!isAccepted ? (
                        <>
                          <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleAcceptBid(bid.id, contractorName, amountDisplay); }}
                            style={{
                              background: '#2563eb',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 13, 
                              padding: '8px 16px', 
                              border: 'none', 
                              borderRadius: 6, 
                              cursor: 'pointer' 
                            }}
                          >
                            Accept Bid
                          </button>
                          <button 
                            type="button"
                            className="btn btn-secondary" 
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await authFetch(getApiUrl(`/api/projects/${bid.projectId}/bids/${bid.id}/quote-pdf`));
                                if (!res.ok) { alert('Failed to load bid PDF'); return; }
                                const blob = await res.blob();
                                const objectUrl = URL.createObjectURL(blob);
                                window.open(objectUrl, '_blank', 'noopener,noreferrer');
                                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                              } catch { alert('Failed to load bid PDF'); }
                            }}
                            style={{ fontSize: 13, padding: '8px 14px' }}
                          >
                            View Details
                          </button>
                          <a
                            href={`mailto:support@corebids.com?subject=${encodeURIComponent(`Bid on ${projectAddress || 'my project'} from ${contractorName}`)}`}
                            style={{ 
                              fontSize: 13, 
                              color: '#4b5563', 
                              textDecoration: 'none',
                              padding: '8px 0'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Message us to ask a question
                          </a>
                        </>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontWeight: 600, fontSize: 14 }}>
                          <span>✓</span>
                          <span>Bid Accepted — Contractor will contact you within 24 hours</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
                // CB:END homeowner-bid-card-revamp
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 18, color: '#6b7280', marginBottom: 16 }}>No bids available yet</div>
              <div style={{ color: '#9ca3af' }}>Bids will appear here once contractors submit their proposals</div>
            </div>
          )}

        </div>
      </div>
      
      {/* CB:BEGIN bid-details-modal */}
      {showDetails && detailsBid && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowDetails(false)}>
          <div className="card" style={{ background: 'white', borderRadius: 12, padding: 24, width: 'min(680px, 94vw)', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>{getContractorName(detailsBid as any, 0)}</h2>
              <button className="btn btn-secondary" onClick={() => setShowDetails(false)}>Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280' }}>Bid Amount</p>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>${(((detailsBid.amountCents ?? detailsBid.amount ?? 0) / 100) * 1.10).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280' }}>Status</p>
                <p style={{ margin: 0 }}>{detailsBid.status || 'PENDING'}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280' }}>Earliest Start</p>
                <p style={{ margin: 0 }}>{detailsBid.earliestStartDate ? new Date(detailsBid.earliestStartDate).toLocaleDateString() : 'TBD'}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280' }}>Estimated Completion</p>
                <p style={{ margin: 0 }}>{detailsBid.estimatedCompletionDate ? new Date(detailsBid.estimatedCompletionDate).toLocaleDateString() : 'TBD'}</p>
              </div>
            </div>
            {(() => {
              const hasNote = detailsBid.note != null && detailsBid.note !== '' && typeof detailsBid.note === 'string';
              const rawSummary = quoteSummaryFromNote(detailsBid.note);
              const showQuote = hasNote || (rawSummary != null && typeof rawSummary === 'object');
              if (!showQuote) return null;
              const safeSummary: QuoteSummaryData | null =
                rawSummary != null && typeof rawSummary === 'object' && !Array.isArray(rawSummary)
                  ? {
                      lineItems: typeof rawSummary.lineItems === 'string' ? rawSummary.lineItems : undefined,
                      timeline: typeof rawSummary.timeline === 'string' ? rawSummary.timeline : undefined,
                      scope: typeof rawSummary.scope === 'string' ? rawSummary.scope : undefined,
                      warranty: typeof rawSummary.warranty === 'string' ? rawSummary.warranty : undefined,
                    }
                  : null;
              return (
                <div style={{ marginTop: 16 }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Quote summary</p>
                  <div style={{ marginTop: 6 }}>
                    <QuoteSummaryBlock summary={safeSummary} fallback={typeof detailsBid.note === 'string' ? detailsBid.note : undefined} />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {/* CB:END bid-details-modal */}
      
      {/* Confirmation Popup */}
      {showPopup && selectedBid && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          {console.log('🔍 Rendering popup with selectedBid:', selectedBid)}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
              Confirm Bid Acceptance
            </h2>
            
            <p style={{ marginBottom: '16px', fontSize: '16px', color: '#374151' }}>
              Are you sure you want to accept the bid from:
            </p>
            
            <div style={{ 
              background: '#f9fafb', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              border: '2px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                {selectedBid.contractor}
              </h3>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                {selectedBid.amount}
              </div>
            </div>
            
            <p style={{ marginBottom: '32px', fontSize: '14px', color: '#6b7280' }}>
              By accepting this bid, you agree to proceed with the project under the terms outlined in the contractor's proposal.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn btn-secondary"
                onClick={handleCancelAccept}
                style={{ padding: '12px 24px' }}
              >
                No
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleConfirmAccept}
                style={{ padding: '12px 24px' }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorBids; 